import React, { useMemo } from "react";
import {
  ResponsiveContainer, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from "recharts";

const fmt = (n) => (n >= 0 ? "+" : "−") + "$" + Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 0 });

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={ttWrap}>
      <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 11 }}>{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{ display: "flex", justifyContent: "space-between", gap: 16, fontSize: 11 }}>
          <span style={{ color: p.color }}>{p.name}</span>
          <span style={{ fontWeight: 600, color: p.value >= 0 ? "#28a745" : "#dc3545" }}>
            {fmt(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

export default function PnLChart({ frames, currentIndex }) {
  const data = useMemo(() => {
    return frames.slice(0, currentIndex + 1).map(f => ({
      date:     f.date?.slice(5),
      realized: Math.round(f.pnl?.realized_options  || 0),
      stock:    Math.round(f.pnl?.unrealised_stock   || 0),
      options:  Math.round(f.pnl?.unrealised_options || 0),
      total:    Math.round(f.pnl?.cumulative         || 0),
    }));
  }, [frames, currentIndex]);

  if (!data.length) return <div style={empty}>No P&L data</div>;

  return (
    <ResponsiveContainer width="100%" height={180}>
      <AreaChart data={data} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id="gRealized" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor="#6f42c1" stopOpacity={0.35} />
            <stop offset="95%" stopColor="#6f42c1" stopOpacity={0.05} />
          </linearGradient>
          <linearGradient id="gStock" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.05} />
          </linearGradient>
          <linearGradient id="gOptions" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor="#fd7e14" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#fd7e14" stopOpacity={0.05} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#999" }} tickLine={false} interval="preserveStartEnd" />
        <YAxis
          tick={{ fontSize: 10, fill: "#999" }}
          tickLine={false}
          tickFormatter={v => `$${(v / 1000).toFixed(0)}k`}
          width={42}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 10 }} />
        <Area type="monotone" dataKey="realized" name="Opts Realized" stroke="#6f42c1" fill="url(#gRealized)" strokeWidth={2} />
        <Area type="monotone" dataKey="stock"    name="Stock (MtM)"   stroke="#3b82f6" fill="url(#gStock)"    strokeWidth={2} />
        <Area type="monotone" dataKey="options"  name="Open Opts"     stroke="#fd7e14" fill="url(#gOptions)"  strokeWidth={2} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

const ttWrap = { background: "#fff", border: "1px solid #e0e0e0", borderRadius: 8, padding: "8px 12px", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" };
const empty  = { height: 180, display: "flex", alignItems: "center", justifyContent: "center", color: "#bbb", fontSize: 13 };
